import { DeliveryAddressSchema } from '../delivery-address/form/schema';
import DeliveryQuotesManager from '../../../../services/api/DeliveryQuotesManager';
import DeliveryZonesManager from '../../../../services/api/DeliveryZonesManager';
import { selectCompany } from '../../../../store/slices/InitialDataSlice';
import { useSelector } from 'react-redux';
import { useMemo, useState } from 'react';
import useGetDeliveryLocation from '../../common/hooks/useGetDeliveryLocation';
import { selectOrderMethodModalData } from '../../../../store/slices/OrderMethodSlice';
import { RenderableErrorResponse } from '../../../../services/exports/Types';
import { useIonToast } from '@ionic/react';
import { ERROR_CODES } from '../../../../services/exports/Constants';
import { useTranslation } from 'react-i18next';

interface OrderMethodValidationError {
  deliveryAddress?: string;
  scheduledFor?: string;
}

const dateTimeErrors: string[] = [
  ERROR_CODES.PICKUP_CLOSED,
  ERROR_CODES.DELIVERY_CLOSED,
  ERROR_CODES.DELIVERY_UNAVAILABLE,
  ERROR_CODES.ORDER_TIME_NOT_AVAILABLE,
  ERROR_CODES.ORDERS_PAUSED,
  ERROR_CODES.COMPANY_CLOSED,
];

const deliveryAddressErrors: string[] = [
  ERROR_CODES.NO_DELIVERY_QUOTES_AVAILABLE,
  ERROR_CODES.ADDRESS_OUTSIDE_DELIVERY_ZONE,
  ERROR_CODES.ADDRESS_NOT_FOUND,
  ERROR_CODES.DELIVERY_QUOTE_ALREADY_USED,
];
const TOAST_DURATION_MS = 5000;

const useDeliveryOrderMethodValidation = () => {
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Shared:Errors',
  });
  const [present] = useIonToast();
  const company = useSelector(selectCompany);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<OrderMethodValidationError>(null);
  const orderMethodModalData = useSelector(selectOrderMethodModalData);
  const { getLocation } = useGetDeliveryLocation(company);

  const handleValidationError = (error: RenderableErrorResponse, genericError: string = t('generic_error')) => {
    const formattedError = error.response?.data?.description || error.response?.data?.title || genericError;

    present({
      message: formattedError,
      duration: TOAST_DURATION_MS,
      position: 'top',
    });

    if (dateTimeErrors.includes(error.response?.data?.error_code)) {
      return { scheduledFor: formattedError };
    }

    if (deliveryAddressErrors.includes(error.response?.data?.error_code)) {
      return { deliveryAddress: formattedError };
    }
  };

  const errorHandler = (error: RenderableErrorResponse) => {
    const formattedError = handleValidationError(error);

    if (!formattedError) {
      return;
    }

    return setValidationError(formattedError);
  };

  async function getDeliveryQuote(location: google.maps.LatLng, data: DeliveryAddressSchema): Promise<boolean> {
    const { success, response } = await DeliveryQuotesManager.create(company?.id, {
      dropoff_time: orderMethodModalData.scheduledFor,
      street_name: data.delivery_street_name,
      street_number: data.delivery_street_number,
      city: data.delivery_city,
      state: company?.state,
      country: company?.country,
      zip_code: data.delivery_zip_code,
      lat: location.lat(),
      lng: location.lng(),
    });

    if (!success) {
      errorHandler(response as any as RenderableErrorResponse);

      return;
    }

    return getDeliveryZone(location, data);
  }

  async function getDeliveryZone(location: google.maps.LatLng, data: DeliveryAddressSchema): Promise<boolean> {
    const { success, response } = await DeliveryZonesManager.search({
      company_id: company?.id,
      lat: location.lat(),
      lng: location.lng(),
      zip: data.delivery_zip_code,
    });

    if (!success) {
      errorHandler(response as any as RenderableErrorResponse);

      return;
    }

    return true;
  }

  async function validate(data: DeliveryAddressSchema): Promise<boolean> {
    setValidationError(null);
    setLoading(true);

    const { success, location } = await getLocation(data);

    if (!success) {
      setLoading(false);
      setValidationError({ deliveryAddress: t('address_not_found') });

      return false;
    }

    const deliveryMethodPromise =
      company?.delivery_mode === 'internal' ? getDeliveryZone(location, data) : getDeliveryQuote(location, data);

    const result = await deliveryMethodPromise;

    setLoading(false);

    return result;
  }

  const isAllDeliveryFieldsFilled = useMemo(
    () =>
      !!orderMethodModalData?.deliveryData &&
      Object.values(orderMethodModalData?.deliveryData).every((value) => !!value),
    [orderMethodModalData?.deliveryData],
  );

  return { validate, loading, validationError, isAllDeliveryFieldsFilled, handleValidationError };
};

export default useDeliveryOrderMethodValidation;
